"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const pages = [
  {
    href: "/about",
    icon: "🏢",
    title: "About Us",
    desc: "Our story, team, values and what makes Pro IT different.",
    tags: ["Our Story", "NZ-Based Team", "Our Values"],
  },
  {
    href: "/case-studies",
    icon: "📊",
    title: "Case Studies",
    desc: "Real results from real New Zealand businesses we've helped.",
    tags: ["Results", "Client Stories", "ROI"],
  },
  {
    href: "/insights",
    icon: "💡",
    title: "Insights",
    desc: "IT guides, tips and expert advice for business owners.",
    tags: ["IT Guides", "Expert Advice", "How-To"],
  },
  {
    href: "/careers",
    icon: "🚀",
    title: "Careers",
    desc: "Join our growing team of IT professionals in Auckland.",
    tags: ["Open Roles", "Perks", "Culture"],
  },
];

export default function CompanyPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Company" },
          ]}
        />
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 50%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Who We Are</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                The Team Behind<br />
                <span className="gradient-text">Your Technology</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>
                Auckland-based IT specialists who&apos;ve been keeping New Zealand businesses running for over a decade.
              </p>
            </FadeInView>
          </div>
        </section>

        {/* Company Pages Grid */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.1}>
              {pages.map((page) => (
                <Link key={page.href} href={page.href}
                  className="group rounded-card-lg p-5 sm:p-8 transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/30 h-full flex flex-col"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="text-4xl mb-4">{page.icon}</div>
                  <h2 className="font-heading font-700 text-[22px] mb-3" style={{ color: "#F8FAFC" }}>{page.title}</h2>
                  <p className="text-[14px] font-body mb-5 leading-[1.75]" style={{ color: "#94A3B8" }}>{page.desc}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {page.tags.map(tag => (
                      <span key={tag} className="px-2.5 py-1 rounded-lg text-[11px] font-body" style={{ background: "rgba(255,255,255,0.05)", color: "rgba(203,213,225,0.7)" }}>{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>
                    Learn more
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </div>
                </Link>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to talk?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Get in touch with our team to discuss how we can help your business.</p>
                <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Strategy Call</Link>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
